import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { QueryClientContext } from "@tanstack/react-query";
import { FlaskConical, Lock, LogIn, ShieldAlert } from "lucide-react";
import { DEV_PERSONAS, useAuth } from "../auth/AuthContext";
import { PARITY_QK, parityApi } from "../api/parityApi";
import { SiteEmblem, VIPER_EXPANSION, ViperEmblem, ViperHeroBackdrop, ViperMark } from "../components/brand";
import { WallDisplayBoardView } from "../components/wall/WallDisplayBoardView";
import { useApiQuery } from "../hooks/useApiQuery";

interface ReturnState {
  returnTo?: string;
  from?: string;
}

/**
 * Sign-in (docs/VIPER/VIPER_BRAND.md §3). Framed over the dark VIPER wireframe hero with the sign-in card seated in
 * the left negative space on deep navy (#060D1A) and slate-50 text for WCAG AA/AAA contrast. The API decides the mode at
 * run time: "Sign in with Microsoft" (Entra ID) in every deployed environment; the Development persona sign-in only when
 * the API runs the Development stub, and it says so in as many words. When Entra is not configured yet, the page says so
 * rather than offering a button that would do nothing.
 */
export function SignInPage() {
  const { principal, signInAsPersona, mode, entraReady, entraUnavailableReason, error, signInWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as ReturnState | null)?.returnTo ?? "/";

  useEffect(() => {
    if (principal) navigate(returnTo, { replace: true });
  }, [principal, navigate, returnTo]);

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-start bg-viper-navy px-4 py-8 text-slate-50 sm:px-8 lg:px-16"
      data-testid="sign-in-page"
    >
      <ViperHeroBackdrop
        testId="sign-in-backdrop"
        overlayClassName="bg-gradient-to-r from-viper-navy/90 via-viper-navy/75 to-viper-navy/35"
      />

      <section
        aria-labelledby="sign-in-heading"
        data-testid="sign-in-card"
        className="relative z-10 w-full max-w-xl rounded-xl border border-viper-cyan/30 bg-viper-navy/95 p-5 text-slate-50 shadow-2xl sm:p-6"
      >
        <div className="mb-4 h-1 w-16 rounded-full bg-viper-crimson" aria-hidden="true" />

        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
          <div className="flex items-center gap-3">
            <SiteEmblem
              size={44}
              alt="Summit Operations emblem"
              className="h-11 w-11 flex-none rounded-full ring-1 ring-viper-cyan/40"
              testId="sign-in-site-emblem"
            />
            <div className="leading-tight">
              <h1 id="sign-in-heading" className="text-lg font-semibold tracking-tight text-slate-50">
                Summit Operations
              </h1>
              <p className="text-xs text-slate-300">Summit Aviation, Inc.</p>
            </div>
          </div>

          <ViperMark
            size={48}
            displaySize={28}
            label="Empowered by VIPER"
            labelClassName="text-xs font-semibold tracking-wide text-viper-cyan"
            testId="sign-in-viper-mark"
          />
        </div>

        {mode === "entra" && entraReady ? (
          <div>
            <p className="text-sm leading-relaxed text-slate-200">Sign in with your Summit Aviation Microsoft account.</p>
            {error && (
              <p role="alert" className="mt-3 rounded-md border border-red-400/60 bg-red-950/90 px-3 py-2 text-sm text-slate-50">
                {error}
              </p>
            )}
            <button
              type="button"
              data-testid="sign-in-microsoft"
              onClick={() => void signInWithMicrosoft?.(returnTo)}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-viper-cyan/60 bg-slate-900/90 px-4 py-2.5 text-sm font-semibold text-slate-50 hover:border-viper-cyan hover:bg-viper-steel/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-viper-cyan"
            >
              <LogIn aria-hidden="true" className="h-4 w-4 text-viper-cyan" />
              Sign in with Microsoft
            </button>
          </div>
        ) : mode === "entra" ? (
          <div role="alert" className="rounded-lg border border-white/15 bg-slate-900/90 p-4 text-slate-50">
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-slate-50">
              <Lock aria-hidden="true" className="h-4 w-4 text-viper-cyan" />
              Microsoft sign-in is not available yet
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-200">{entraUnavailableReason}</p>
          </div>
        ) : (
          <div>
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-amber-300">
              <FlaskConical aria-hidden="true" className="h-4 w-4" />
              Development sign-in
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-200">
              Choose a persona to work as. The server decides which roles each persona holds, exactly as Microsoft Entra ID will once the Summit Operations app
              registration exists (phase P1, blocked on Q-O8). This sign-in is refused outside Development.
            </p>
            {error && (
              <p role="alert" className="mt-3 rounded-md border border-red-400/60 bg-red-950/90 px-3 py-2 text-sm text-slate-50">
                {error}
              </p>
            )}
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {DEV_PERSONAS.filter((p) => p.key !== "anonymous").map((p) => (
                <li key={p.key}>
                  <button
                    type="button"
                    data-testid={`sign-in-${p.key}`}
                    onClick={async () => {
                      const ok = await signInAsPersona(p.key);
                      if (ok) navigate(returnTo, { replace: true });
                    }}
                    className="flex w-full flex-col items-start gap-0.5 rounded-lg border border-slate-700/80 bg-slate-900/90 px-3 py-2 text-left text-slate-50 hover:border-viper-cyan hover:bg-viper-steel/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-viper-cyan"
                  >
                    <span className="text-sm font-medium text-slate-50">{p.label}</span>
                    <span className="text-[11px] leading-snug text-slate-300">{p.capability}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

/** Signed in, but the role does not include this screen. The API refuses it too — this page just says so kindly. */
export function NotAuthorizedPage() {
  const location = useLocation();
  const from = (location.state as ReturnState | null)?.from;
  return (
    <div className="mx-auto mt-16 max-w-xl px-4">
      <div className="card !p-5" role="alert">
        <p className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
          <ShieldAlert aria-hidden="true" className="h-4 w-4 text-amber-600" />
          Your role does not include this screen
        </p>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          {from ? `${from} is not part of what your roles may open.` : "This screen is not part of what your roles may open."} Nothing was changed. If you need
          it, ask the Summit Operations administrator for the role that covers it.
        </p>
        <Link to="/" className="btn-primary mt-4 inline-block">
          Back to my start page
        </Link>
      </div>
    </div>
  );
}

/** Signed in from the directory with no Operations role at all. */
export function RequestAccessPage() {
  const { principal } = useAuth();
  return (
    <div className="mx-auto mt-16 max-w-xl px-4">
      <div className="card !p-5">
        <p className="text-sm font-semibold text-slate-900">You do not have a Summit Operations role yet</p>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          {principal?.displayName ? `${principal.displayName}, you are ` : "You are "}signed in from Summit's directory, but no Operations role has been granted
          to you. Ask the Operations Manager or the Summit Operations administrator for the role that matches your job — Planner, Shop Supervisor or Operations
          Viewer.
        </p>
        <p className="mt-2 text-xs text-slate-500">Nothing on this site is visible without a role, and a role is always granted by a person.</p>
      </div>
    </div>
  );
}

/** The kiosk principal has exactly one destination and it is not an application page (D-O86, O-R34). */
export function WallDisplayPage() {
  const params = useParams<{ displayKey?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKey = params.displayKey ?? searchParams.get("key") ?? "shop-ms1";
  const [selectedKey, setSelectedKey] = useState(initialKey);
  const queryClient = useContext(QueryClientContext);

  useEffect(() => {
    const next = params.displayKey ?? searchParams.get("key");
    if (next && next !== selectedKey) {
      setSelectedKey(next);
    }
  }, [params.displayKey, searchParams, selectedKey]);

  const handleSelectKey = (nextKey: string) => {
    setSelectedKey(nextKey);
    setSearchParams({ key: nextKey }, { replace: true });
  };

  return (
    <div className="wall-surface relative flex min-h-screen flex-col bg-viper-navy text-slate-50" data-testid="wall-display">
      <ViperHeroBackdrop testId="wall-backdrop" overlayClassName="bg-viper-navy/70" />

      <header
        data-testid="wall-display-header"
        className="relative z-10 flex items-center justify-between gap-4 border-b border-white/10 bg-viper-navy/80 px-6 py-4"
      >
        <div className="flex items-center gap-3">
          <SiteEmblem
            size={48}
            alt="Summit Operations emblem"
            className="h-12 w-12 flex-none rounded-full ring-1 ring-white/15"
            testId="wall-site-emblem"
          />
          <div className="leading-tight text-left">
            <h1 className="text-lg font-semibold tracking-tight text-slate-50">
              Summit Operations — shop floor board
            </h1>
            <div className="text-xs text-slate-300">Summit Aviation, Inc. · Shop floor wall display · {selectedKey}</div>
          </div>
        </div>

        <ViperEmblem
          size={128}
          displaySize={128}
          label="VIPER"
          subtitle={VIPER_EXPANSION}
          imgAlt="Summit VIPER emblem"
          testId="wall-viper-emblem"
        />
      </header>

      <main className="relative z-10 flex flex-1 flex-col">
        {queryClient ? (
          <WallDisplayRuntimeLoader
            displayKey={selectedKey}
            onSelectDisplayKey={handleSelectKey}
          />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
            <p className="max-w-2xl text-lg leading-relaxed text-slate-200">
              This kiosk sign-in reaches exactly one aggregated endpoint and no other screen, so nothing personal can be opened from here.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function WallDisplayRuntimeLoader({
  displayKey,
  onSelectDisplayKey
}: {
  displayKey: string;
  onSelectDisplayKey: (key: string) => void;
}) {
  const { loading } = useAuth();
  const query = useApiQuery(
    PARITY_QK.wallBoard(displayKey),
    () => parityApi.getWallBoard(displayKey),
    { enabled: !loading, staleTime: 15_000 }
  );

  if (loading || query.isPending || query.isError || !query.data) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center" data-testid="wall-board-loading">
        <p className="max-w-2xl text-lg leading-relaxed text-slate-200">
          This kiosk sign-in reaches exactly one aggregated endpoint and no other screen, so nothing personal can be opened from here.
        </p>
      </div>
    );
  }

  return (
    <WallDisplayBoardView
      board={query.data}
      selectedDisplayKey={query.data.displayKey}
      onSelectDisplayKey={onSelectDisplayKey}
    />
  );
}

export function NotFoundPage() {
  return (
    <div className="mx-auto mt-16 max-w-xl px-4">
      <div className="card !p-5">
        <p className="text-sm font-semibold text-slate-900">There is no screen at this address</p>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          The link may be from an older version of Summit Operations. Every screen that has ever moved keeps a redirect, so if this one was real it should have
          been sent on — please tell the administrator which link brought you here.
        </p>
        <Link to="/" className="btn-primary mt-4 inline-block">
          Back to my start page
        </Link>
      </div>
    </div>
  );
}
